import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Download, X, CheckCircle2, ZoomIn } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const isImg = (v) => (v?.content_type || '').startsWith('image/') || /\.(png|jpe?g|webp|gif|heic)$/i.test(v?.name || '');

const useBlobUrl = (reqId, slot, enabled, headers) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!enabled) return;
    let u;
    axios.get(`${API}/m/requests/${reqId}/files/${encodeURIComponent(slot)}`, { headers, responseType: 'blob' }).then(r => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [reqId, slot, enabled]);
  return url;
};

const Thumb = ({ reqId, slot, info, headers, isRTL, onOpen }) => {
  const img = isImg(info);
  const url = useBlobUrl(reqId, slot, img, headers);
  const OFFICIAL = { receipt: isRTL ? 'إيصال الدفع الحكومي' : 'Gov. payment receipt', approval: isRTL ? 'مستند الموافقة' : 'Approval document', rejection: isRTL ? 'مستند الرفض' : 'Rejection document' };
  const label = slot === 'transfer_receipt' ? (isRTL ? 'إيصال التحويل' : 'Transfer receipt') : slot === 'official_document' ? (isRTL ? 'المستند الرسمي' : 'Official document') : slot.startsWith('official:') ? (OFFICIAL[slot.split(':')[1]] || slot) : slot;
  const tone = slot === 'transfer_receipt' ? 'border-emerald-300 bg-emerald-50' : slot === 'official_document' ? 'border-[#D4AF37] bg-amber-50' : 'border-slate-200 bg-slate-50';
  return (
    <button onClick={() => onOpen({ slot, info, url, label })} className={`w-[118px] rounded-xl border overflow-hidden text-start group ${tone}`} data-testid={`request-file-${reqId}-${slot}`}>
      <div className="h-[86px] bg-white flex items-center justify-center relative overflow-hidden">
        {img ? (url ? <img src={url} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-6 h-6 rounded-full border-2 border-slate-200 border-t-slate-500 animate-spin" />) : <FileText className="w-8 h-8 text-slate-400" />}
        <span className="absolute bottom-1 end-1 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center"><ZoomIn className="w-3 h-3" /></span>
      </div>
      <div className="px-2 py-1.5"><p className="text-[11px] font-bold text-slate-800 truncate flex items-center gap-1">{slot === 'transfer_receipt' && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}{label}</p><p className="text-[10px] text-slate-500 truncate" dir="ltr">{info?.name}</p></div>
    </button>
  );
};

export const RequestFiles = ({ r, headers, isRTL }) => {
  const [open, setOpen] = useState(null);
  const files = Object.entries(r.files || {});
  const fullUrl = useBlobUrl(r.id, open?.slot || '', !!open && !open.url, headers);
  const src = open?.url || fullUrl;
  if (!files.length) return null;
  const download = () => { if (!src) return; const a = document.createElement('a'); a.href = src; a.download = open.info?.name || open.slot; a.click(); };
  return (
    <>
      <div className="flex flex-wrap gap-2" data-testid={`request-files-${r.id}`}>
        {files.map(([k, v]) => <Thumb key={k} reqId={r.id} slot={k} info={v} headers={headers} isRTL={isRTL} onOpen={setOpen} />)}
      </div>
      {open && (
        <div className="fixed inset-0 z-[200] bg-black/85 flex flex-col" dir={isRTL ? 'rtl' : 'ltr'} onClick={() => setOpen(null)} data-testid="file-lightbox">
          <div className="flex items-center justify-between px-4 py-3 text-white" onClick={e => e.stopPropagation()}>
            <div className="min-w-0"><p className="text-sm font-bold truncate">{open.label}</p><p className="text-[11px] text-white/60 truncate" dir="ltr">{open.info?.name}</p></div>
            <div className="flex items-center gap-2">
              <button onClick={download} disabled={!src} className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-bold flex items-center gap-1 disabled:opacity-40" data-testid="file-lightbox-download"><Download className="w-4 h-4" />{isRTL ? 'تحميل' : 'Download'}</button>
              <button onClick={() => setOpen(null)} className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="file-lightbox-close"><X className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="flex-1 min-h-0 flex items-center justify-center p-4" onClick={e => e.stopPropagation()}>
            {!src ? <div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : isImg(open.info) ? <img src={src} alt={open.label} className="max-w-full max-h-full object-contain rounded-lg shadow-2xl" /> : <iframe title={open.label} src={src} className="w-full h-full rounded-lg bg-white" />}
          </div>
        </div>
      )}
    </>
  );
};
